import type { CreateInstanceRequest, Instance, UpdateInstanceRequest } from '@/types/instance';
import type { APIKey, APIKeyRules, CreateAPIKeyRequest, CreateAPIKeyResponse } from '@/types/apiKey';
import type { AuditLog, APIRequestLog, APIKeyActivity, AuditActionInfo, LogFilters, Paginated } from '@/types/auditLog';
import type { Deployment, DeploymentLog } from '@/types/deployment';
import type { Volume } from '@/types/volume';
import type { Network } from '@/types/network';
import type { Container, ContainerStats } from '@/types/container';
import type { Stack, Service } from '@/types/stack';
import type { Endpoint, EndpointResource, EndpointResourceResponse, EndpointWithResources } from '@/types/endpoint';
import type { App, DeployRequest, DeployResponse, AppStatsResponse, AppContainerResponse } from '@/types/app';
import { collectAllPages } from '@/lib/paging';
import type { ExternalUser, ExternalUserAppsResponse } from '@/types/externalUser';

import type { Catalog, CatalogStack, CreateCatalogRequest, UpdateCatalogRequest, CreateCatalogStackRequest, UpdateCatalogStackRequest } from '@/types/catalog';
import type { SecurityBlock, CreateSecurityBlockRequest, UpdateSecurityBlockRequest } from '@/types/security-block';
import type { TLSSettings } from '@/types/tlsSettings';
import type {
  CreateDomainRequest,
  DomainDetail,
  DomainSummary,
  DomainSwitch,
  DomainSwitchRequest,
  ReservedSubdomain,
  UpdateDomainRequest,
  UploadOriginCertRequest,
} from '@/types/domain';
import type {
  CreateWebhookReceiverRequest,
  EventListParams,
  EventsPage,
  UpdateWebhookReceiverRequest,
  WebhookDelivery,
  WebhookDeliveryListParams,
  WebhookReceiver,
  WebhookReceiverListParams,
  WebhookReceiverSummary,
  WebhookReceiverWithSecret,
} from '@/types/webhook';

const API_BASE = '/api/v1';

/** Error thrown for any non-2xx API response; `status` is the HTTP status, `code` the backend error code (e.g. NOT_FOUND). */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const apiKey = localStorage.getItem('apiKey');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(apiKey ? { 'X-API-Key': apiKey } : {}),
    ...options?.headers,
  };

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  let data: { data?: unknown; error?: { code?: string; message?: string; details?: unknown } } | null;
  try {
    data = await res.json();
  } catch {
    // Non-JSON body (e.g. an HTML 502 from a proxy).
    if (!res.ok) throw new ApiError(`Request failed (HTTP ${res.status})`, res.status);
    throw new ApiError('Invalid response from server', res.status);
  }

  if (!res.ok) {
    throw new ApiError(data?.error?.message || 'API Error', res.status, data?.error?.code, data?.error?.details);
  }
  return data?.data as T;
}

function buildQuery(params?: object): string {
  const query = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
  });
  const s = query.toString();
  return s ? `?${s}` : '';
}

// Backend returns a paginated envelope: { data: [...], total, limit, offset }.
function listEndpointsPage<T>(params?: { instance_id?: number; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.instance_id) query.set('instance_id', params.instance_id.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return apiFetch<{ data: T[]; total: number; limit: number; offset: number }>(
    `/endpoints${query.toString() ? '?' + query.toString() : ''}`
  );
}

export const api = {
  /**
   * GET a server-paginated list. Unwraps the `{ data, total, limit, offset }` envelope; a bare array
   * (unpaginated resource) becomes `{ data, total: length }`; any other shape yields an empty page.
   * Failures are `ApiError`s (via `apiFetch`).
   */
  paged: async <T>(resource: string, params: Record<string, string | number>, signal?: AbortSignal): Promise<{ data: T[]; total: number }> => {
    const payload = await apiFetch<unknown>(`/${resource}${buildQuery(params)}`, signal ? { signal } : undefined);
    if (Array.isArray(payload)) return { data: payload as T[], total: payload.length };
    if (payload !== null && typeof payload === 'object') {
      const env = payload as { data?: unknown; total?: unknown };
      if (Array.isArray(env.data)) {
        const total = typeof env.total === 'number' && Number.isFinite(env.total) ? env.total : env.data.length;
        return { data: env.data as T[], total };
      }
    }
    return { data: [], total: 0 };
  },
  instances: {
    list: () => apiFetch<Instance[]>('/instances'),
    get: (id: number) => apiFetch<Instance>(`/instances/${id}`),
    create: (data: CreateInstanceRequest) =>
      apiFetch<Instance>('/instances', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: UpdateInstanceRequest) =>
      apiFetch<Instance>(`/instances/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      apiFetch<void>(`/instances/${id}`, { method: 'DELETE' }),
    triggerSync: (id: number) =>
      apiFetch<{ triggered: boolean }>(`/instances/${id}/sync`, { method: 'POST' }),
  },
  apiKeys: {
    list: () => apiFetch<APIKey[]>('/api-keys'),
    create: (data: CreateAPIKeyRequest) =>
      apiFetch<CreateAPIKeyResponse>('/api-keys', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: CreateAPIKeyRequest) =>
      apiFetch<APIKey>(`/api-keys/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    regenerate: (id: number) =>
      apiFetch<CreateAPIKeyResponse>(`/api-keys/${id}/regenerate`, {
        method: 'POST',
      }),
    delete: (id: number) => apiFetch<void>(`/api-keys/${id}`, { method: 'DELETE' }),
    activity: (id: number) => apiFetch<APIKeyActivity>(`/api-keys/${id}/activity`),
  },
  me: {
    get: () => apiFetch<{ name: string; is_master: boolean } & APIKeyRules>('/me'),
  },
  auditLogs: {
    list: (filters?: LogFilters) =>
      apiFetch<Paginated<AuditLog>>(`/audit-logs${buildQuery(filters)}`),
    get: (id: number) => apiFetch<AuditLog>(`/audit-logs/${id}`),
    meta: () => apiFetch<{ actions: AuditActionInfo[] }>('/audit-logs/meta'),
  },
  requestLogs: {
    list: (filters?: LogFilters) =>
      apiFetch<Paginated<APIRequestLog>>(`/request-logs${buildQuery(filters)}`),
  },
  deployments: {
    list: (params?: { instance_id?: number; status?: string }) => {
      const query = new URLSearchParams();
      if (params?.instance_id) query.set('instance_id', params.instance_id.toString());
      if (params?.status) query.set('status', params.status);
      return apiFetch<Deployment[]>(`/deployments?${query.toString()}`);
    },
    get: (id: number) => apiFetch<Deployment>(`/deployments/${id}`),
    getLogs: (id: number) => apiFetch<DeploymentLog[]>(`/deployments/${id}/logs`),
  },
  volumes: {
    list: (params?: { instance_id?: number; endpoint_id?: number }) => {
      const query = new URLSearchParams();
      if (params?.instance_id) query.set('instance_id', params.instance_id.toString());
      if (params?.endpoint_id) query.set('endpoint_id', params.endpoint_id.toString());
      return apiFetch<Volume[]>(`/volumes${query.toString() ? '?' + query.toString() : ''}`);
    },
    get: (id: number) => apiFetch<Volume>(`/volumes/${id}`),
    create: (data: { name: string; instance_id: number; endpoint_id: number; driver?: string }) =>
      apiFetch<Volume>('/volumes', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      apiFetch<void>(`/volumes/${id}`, { method: 'DELETE' }),
    getContainers: (id: number) =>
      apiFetch<Container[]>(`/volumes/${id}/containers`),
  },
  networks: {
    list: (params?: { instance_id?: number; endpoint_id?: number }) => {
      const query = new URLSearchParams();
      if (params?.instance_id) query.set('instance_id', params.instance_id.toString());
      if (params?.endpoint_id) query.set('endpoint_id', params.endpoint_id.toString());
      return apiFetch<Network[]>(`/networks${query.toString() ? '?' + query.toString() : ''}`);
    },
    get: (id: number) => apiFetch<Network>(`/networks/${id}`),
    create: (data: { name: string; instance_id: number; endpoint_id: number; driver?: string }) =>
      apiFetch<Network>('/networks', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      apiFetch<void>(`/networks/${id}`, { method: 'DELETE' }),
    getContainers: (id: number) =>
      apiFetch<Container[]>(`/networks/${id}/containers`),
  },
  containers: {
    list: (params?: { instance_id?: number; endpoint_id?: number; stack_id?: number; status?: string; limit?: number }) => {
      const query = new URLSearchParams();
      if (params?.instance_id) query.set('instance_id', params.instance_id.toString());
      if (params?.endpoint_id) query.set('endpoint_id', params.endpoint_id.toString());
      if (params?.stack_id) query.set('stack_id', params.stack_id.toString());
      if (params?.status) query.set('status', params.status);
      if (params?.limit) query.set('limit', params.limit.toString());
      // Backend returns a paginated envelope (default limit 20, max 100); unwrap to the array.
      return apiFetch<{ data: Container[]; total: number; limit: number; offset: number }>(`/containers${query.toString() ? '?' + query.toString() : ''}`)
        .then(result => result.data);
    },
    get: (id: number) => apiFetch<Container>(`/containers/${id}`),
    stats: (id: number, params?: { range?: string }) => {
      const query = new URLSearchParams();
      if (params?.range) query.set('range', params.range);
      return apiFetch<ContainerStats>(`/containers/${id}/stats${query.toString() ? '?' + query.toString() : ''}`);
    },
    restart: (id: number) =>
      apiFetch<void>(`/containers/${id}/restart`, { method: 'POST' }),
    stop: (id: number) =>
      apiFetch<void>(`/containers/${id}/stop`, { method: 'POST' }),
    start: (id: number) =>
      apiFetch<void>(`/containers/${id}/start`, { method: 'POST' }),
  },
  stacks: {
    stats: (id: number, params?: { range?: string }) => 
      apiFetch<AppStatsResponse>(`/stacks/${id}/stats${params?.range ? `?range=${params.range}` : ''}`),
    list: (params?: { instance_id?: number; endpoint_id?: number; type?: string }) => {
      const query = new URLSearchParams();
      if (params?.instance_id) query.set('instance_id', params.instance_id.toString());
      if (params?.endpoint_id) query.set('endpoint_id', params.endpoint_id.toString());
      if (params?.type) query.set('type', params.type);
      return apiFetch<Stack[]>(`/stacks${query.toString() ? '?' + query.toString() : ''}`);
    },
    get: (id: number) => apiFetch<Stack>(`/stacks/${id}`),
    // The server caps pages at 100; this walks every page (limit/offset).
    listAll: () =>
      collectAllPages<Stack>(async (i) => {
        const r = await api.paged<Stack>('stacks', { limit: 100, offset: i * 100 });
        return { items: r.data, total: r.total };
      }),
  },
  services: {
    list: (params?: { stack_id?: number }) => {
      const query = new URLSearchParams();
      if (params?.stack_id) query.set('stack_id', params.stack_id.toString());
      return apiFetch<Service[]>(`/services${query.toString() ? '?' + query.toString() : ''}`);
    },
    get: (id: number) => apiFetch<Service>(`/services/${id}`),
    scale: (id: number, replicas: number) =>
      apiFetch<void>(`/services/${id}/scale`, {
        method: 'POST',
        body: JSON.stringify({ replicas }),
      }),
    restart: (id: number) =>
      apiFetch<void>(`/services/${id}/restart`, { method: 'POST' }),
  },
  endpoints: {
    // The server caps pages at 100; these walk every page (limit/offset).
    listAll: () =>
      collectAllPages<Endpoint>(async (i) => {
        const r = await listEndpointsPage<Endpoint>({ limit: 100, offset: i * 100 });
        return { items: r.data, total: r.total };
      }),
    listWithResourcesAll: () =>
      collectAllPages<EndpointWithResources>(async (i) => {
        const r = await listEndpointsPage<EndpointWithResources>({ limit: 100, offset: i * 100 });
        return { items: r.data, total: r.total };
      }),
    get: (id: number) => apiFetch<Endpoint>(`/endpoints/${id}`),
  },
  endpointResources: {
    get: (endpointId: number) =>
      apiFetch<EndpointResourceResponse>(`/endpoints/${endpointId}/resources`),
    update: (endpointId: number, data: {
      cpu_available: number;
      memory_available_mb: number;
      storage_available_gb: number;
    }) =>
      apiFetch<EndpointResource>(`/endpoints/${endpointId}/resources`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    reset: (endpointId: number) =>
      apiFetch<EndpointResource>(`/endpoints/${endpointId}/resources/reset`, {
        method: 'POST',
      }),
  },
  catalog: {
    list: (params?: { show_deleted?: boolean }) => {
      const query = new URLSearchParams();
      if (params?.show_deleted) query.set('show_deleted', 'true');
      return apiFetch<Catalog[]>(`/catalog${query.toString() ? '?' + query.toString() : ''}`);
    },
    get: (id: number) => apiFetch<Catalog>(`/catalog/${id}`),
    create: (data: CreateCatalogRequest) =>
      apiFetch<Catalog>('/catalog', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: UpdateCatalogRequest) =>
      apiFetch<Catalog>(`/catalog/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      apiFetch<void>(`/catalog/${id}`, { method: 'DELETE' }),
    restore: (id: number) =>
      apiFetch<void>(`/catalog/${id}/restore`, { method: 'POST' }),
    
    stacks: {
      list: (catalogId: number, params?: { show_deleted?: boolean }) => {
        const query = new URLSearchParams();
        if (params?.show_deleted) query.set('show_deleted', 'true');
        return apiFetch<CatalogStack[]>(`/catalog/${catalogId}/stacks${query.toString() ? '?' + query.toString() : ''}`);
      },
      get: (catalogId: number, stackId: number) =>
        apiFetch<CatalogStack>(`/catalog/${catalogId}/stacks/${stackId}`),
      create: (catalogId: number, data: CreateCatalogStackRequest) =>
        apiFetch<CatalogStack>(`/catalog/${catalogId}/stacks`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (catalogId: number, stackId: number, data: UpdateCatalogStackRequest) =>
        apiFetch<CatalogStack>(`/catalog/${catalogId}/stacks/${stackId}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      delete: (catalogId: number, stackId: number) =>
        apiFetch<{ message: string; warning?: string; apps_using?: number }>(`/catalog/${catalogId}/stacks/${stackId}`, { method: 'DELETE' }),
      restore: (catalogId: number, stackId: number) =>
        apiFetch<void>(`/catalog/${catalogId}/stacks/${stackId}/restore`, { method: 'POST' }),
    },
  },
  settings: {
    tls: {
      get: () => apiFetch<TLSSettings>('/settings/tls'),
      push: () => apiFetch<{ triggered: boolean }>('/settings/tls/push', { method: 'POST' }),
    },
  },
  domains: {
    /** Default domain first. */
    list: () => apiFetch<DomainSummary[]>('/domains'),
    get: (id: number) => apiFetch<DomainDetail>(`/domains/${id}`),
    create: (data: CreateDomainRequest) =>
      apiFetch<DomainDetail>('/domains', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: UpdateDomainRequest) =>
      apiFetch<DomainDetail>(`/domains/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => apiFetch<{ deleted: boolean; id: number }>(`/domains/${id}`, { method: 'DELETE' }),
    uploadOriginCert: (id: number, data: UploadOriginCertRequest) =>
      apiFetch<DomainDetail>(`/domains/${id}/origin-cert`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteOriginCert: (id: number) => apiFetch<DomainDetail>(`/domains/${id}/origin-cert`, { method: 'DELETE' }),
  },
  reservedSubdomains: {
    list: () => apiFetch<ReservedSubdomain[]>('/reserved-subdomains'),
    create: (label: string) =>
      apiFetch<ReservedSubdomain>('/reserved-subdomains', { method: 'POST', body: JSON.stringify({ label }) }),
    delete: (id: number) =>
      apiFetch<{ deleted: boolean; id: number }>(`/reserved-subdomains/${id}`, { method: 'DELETE' }),
  },
  securityBlocks: {
    list: () => apiFetch<SecurityBlock[]>('/security-blocks'),
    get: (id: number) => apiFetch<SecurityBlock>(`/security-blocks/${id}`),
    create: (data: CreateSecurityBlockRequest) =>
      apiFetch<SecurityBlock>('/security-blocks', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: number, data: UpdateSecurityBlockRequest) =>
      apiFetch<SecurityBlock>(`/security-blocks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      apiFetch<void>(`/security-blocks/${id}`, { method: 'DELETE' }),
    restore: (id: number) =>
      apiFetch<SecurityBlock>(`/security-blocks/${id}/restore`, { method: 'POST' }),
  },
  webhooks: {
    list: (params?: WebhookReceiverListParams) =>
      apiFetch<Paginated<WebhookReceiverSummary>>(`/webhook-receivers${buildQuery(params)}`),
    get: (id: number) => apiFetch<WebhookReceiver>(`/webhook-receivers/${id}`),
    create: (data: CreateWebhookReceiverRequest) =>
      apiFetch<WebhookReceiverWithSecret>('/webhook-receivers', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: UpdateWebhookReceiverRequest) =>
      apiFetch<WebhookReceiver>(`/webhook-receivers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => apiFetch<{ deleted: boolean }>(`/webhook-receivers/${id}`, { method: 'DELETE' }),
    rotateSecret: (id: number) =>
      apiFetch<WebhookReceiverWithSecret>(`/webhook-receivers/${id}/rotate-secret`, { method: 'POST' }),
    /** Queues a ping to this receiver (202); 409 RECEIVER_INACTIVE if it is inactive. */
    test: (id: number) => apiFetch<{ delivery_id: number }>(`/webhook-receivers/${id}/test`, { method: 'POST' }),
    deliveries: (id: number, params?: WebhookDeliveryListParams) =>
      apiFetch<Paginated<WebhookDelivery>>(`/webhook-receivers/${id}/deliveries${buildQuery(params)}`),
    /** Queues a new delivery row (202); 409 DELIVERY_PENDING or RECEIVER_INACTIVE. */
    redeliver: (deliveryId: number) =>
      apiFetch<WebhookDelivery>(`/webhook-deliveries/${deliveryId}/redeliver`, { method: 'POST' }),
  },
  events: {
    list: (params?: EventListParams) => apiFetch<EventsPage>(`/events${buildQuery(params)}`),
  },
  apps: {
    stats: (id: number, params?: { range?: string }) => 
      apiFetch<AppStatsResponse>(`/apps/${id}/stats${params?.range ? `?range=${params.range}` : ''}`),
    containers: (id: number) =>
      apiFetch<AppContainerResponse[]>(`/apps/${id}/containers`),
    deploy: (data: DeployRequest) =>
      apiFetch<DeployResponse>('/apps/deploy', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    get: (id: number) => apiFetch<App>(`/apps/${id}`),
    list: (params?: { external_user_id?: string; status?: string; page?: number; limit?: number }) => {
      const query = new URLSearchParams();
      if (params?.external_user_id) query.set('external_user_id', params.external_user_id);
      if (params?.status) query.set('status', params.status);
      if (params?.page) query.set('page', params.page.toString());
      if (params?.limit) query.set('limit', params.limit.toString());
      return apiFetch<{ data: App[]; page: number; limit: number; total: number; total_pages: number }>(
        `/apps${query.toString() ? '?' + query.toString() : ''}`
      );
    },
    // The server caps pages at 100 (and resets larger limits to 20); walk every page.
    listAll: (params?: { external_user_id?: string; status?: string }) =>
      collectAllPages<App>(async (i) => {
        const r = await api.apps.list({ ...params, page: i + 1, limit: 100 });
        return { items: r.data, total: r.total };
      }),
    stop: (id: number) =>
      apiFetch<void>(`/apps/${id}/stop`, { method: 'POST' }),
    start: (id: number) =>
      apiFetch<void>(`/apps/${id}/start`, { method: 'POST' }),
    redeploy: (id: number, envVars?: Record<string, string>) =>
      apiFetch<DeployResponse>(
        `/apps/${id}/redeploy`,
        {
          method: 'POST',
          body: envVars ? JSON.stringify({ env_vars: envVars }) : undefined,
        }
      ),
    scale: (id: number, stackName: string) =>
      apiFetch<void>(`/apps/${id}/scale`, {
        method: 'POST',
        body: JSON.stringify({ stack_name: stackName }),
      }),
    logs: (id: number, tail?: number) => {
      const query = tail ? `?tail=${tail}` : '';
      return apiFetch<{ logs: Array<{ timestamp: string, level: string, message: string }> }>(`/apps/${id}/logs${query}`);
    },
    containerLogs: (id: number, containerId: number, tail?: number) => {
      const query = tail ? `?tail=${tail}` : '';
      return apiFetch<{ logs: string; empty: boolean; hint: string | null }>(
        `/apps/${id}/containers/${containerId}/logs${query}`,
      );
    },
    delete: (id: number) =>
      apiFetch<void>(`/apps/${id}`, { method: 'DELETE' }),
    /** Queues a move to another domain (202). */
    switchDomain: (id: number, data: DomainSwitchRequest) =>
      apiFetch<DomainSwitch>(`/apps/${id}/domain-switch`, { method: 'POST', body: JSON.stringify(data) }),
    /** Latest domain switch; 404 DOMAIN_SWITCH_NOT_FOUND when the app never had one. */
    domainSwitch: (id: number) => apiFetch<DomainSwitch>(`/apps/${id}/domain-switch`),
  },
  externalUsers: {
    list: () => apiFetch<ExternalUser[]>('/external-users'),
    get: (id: number) => apiFetch<ExternalUser>(`/external-users/${id}`),
    update: (id: number, data: { display_name: string }) =>
      apiFetch<ExternalUser>(`/external-users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    getApps: (id: number, params?: { status?: string; endpoint_id?: number; search?: string; page?: number; limit?: number }) => {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      if (params?.endpoint_id) query.set('endpoint_id', params.endpoint_id.toString());
      if (params?.search) query.set('search', params.search);
      if (params?.page) query.set('page', params.page.toString());
      if (params?.limit) query.set('limit', params.limit.toString());
      return apiFetch<ExternalUserAppsResponse>(`/external-users/${id}/apps${query.toString() ? '?' + query.toString() : ''}`);
    },
  },
};